'use client';

import { PropsWithChildren, useRef } from 'react';
import { Box } from '@wishket/design-system';
import dynamic from 'next/dynamic';

import { ArticleToolbarComment } from './ArticleDetailMain.parts';

import Advertise from '@/widgets/Advertise';
import { PATH } from '@/shared/constants';
import type { ArticleStatisticsData } from '@/entities/article';
import { Skeleton } from '@/shared/Components';
import { ArticleDetailHeader } from '@/features/ArticleDetail';

const ArticleDetailSideToolbarClient = dynamic(
  () =>
    import('./ArticleDetailMain.parts').then(
      (mod) => mod.ArticleDetailSideToolbar
    ),
  {
    ssr: false,
    loading: () => (
      <Skeleton className="hidden h-[245px] w-[56px] desktop:block" />
    ),
  }
);

const ArticleStatistics = dynamic(() => import('@/widgets/ArticleStatistics'), {
  ssr: false,
  loading: () => <Skeleton className="h-[245px] w-full" />,
});

interface ArticleDetailCommentWithToolbarProps extends PropsWithChildren {
  id: string;
  category: {
    name: string;
    flag: string;
  };
  title: string;
  author: {
    name: string;
    urlIdentifier: string;
    imgUrl: string;
  };
  time: number;
  date: string;
  isPopular: boolean;
  viewCount: number;
  statisticsData: ArticleStatisticsData | null;
}

const ArticleDetailMain = ({
  id,
  category,
  title,
  author,
  time,
  date,
  isPopular,
  viewCount,
  statisticsData,
  children,
}: ArticleDetailCommentWithToolbarProps) => {
  const commentListRef = useRef<HTMLDivElement>(null);

  return (
    <>
      <article
        id="article-detail-container"
        className="box-border flex w-full flex-col gap-8 grid-in-content desktop:min-w-[744px]"
      >
        <ArticleDetailHeader
          categoryName={category.name}
          categoryHref={`${PATH.LIST}/${category.flag}`}
          title={title}
          authorName={author.name}
          authorHref={`${PATH.HOME}/@${author.urlIdentifier}`}
          authorImgUrl={author.imgUrl}
          time={time}
          date={date}
          isPopular={isPopular}
          viewCount={viewCount}
        />
        <ArticleStatistics data={statisticsData} />
        <div id="article-detail-server" className="w-full">
          {children}
        </div>
        <Box className="hidden desktop:block">
          <Advertise location="content_detail_3" />
        </Box>
        <ArticleToolbarComment
          id={id}
          title={title}
          commentListRef={commentListRef}
        />
      </article>
      <aside
        className="box-border min-w-[72px] pt-1.5 desktop:grid-in-toolbar"
        id="article-detail-toolbar"
      >
        <ArticleDetailSideToolbarClient
          commentListRef={commentListRef}
          id={id}
          articleTitle={title}
        />
      </aside>
    </>
  );
};

export default ArticleDetailMain;
